import { useId, useRef } from 'react';

import { Money, formatDate, formatMoney } from '@betterment/core';
import { Box, Shimmer } from '@betterment/samba-react';
import {
  HiddenChartDescription,
  PlaceholderAreaChart,
  BalanceChart as SharedBalanceChart,
} from '@betterment/shared-components';
import { ParentSize } from '@visx/responsive';

import { UserBalancePerformanceQuery } from './graphql/userPerformanceBalance.generated';

import { trackHover } from '../../../../features/track';

export type BalanceChartProps = {
  history?: UserBalancePerformanceQuery['performance']['history'];
  loading: boolean;
  startingBalance?: Money;
  endingBalance?: Money;
  netDeposits?: Money;
  totalEarned?: Money;
  adjustments?: Money;
};

const BalanceChartDescription: React.FC<{
  id: string;
  startDate: Date;
  endDate: Date;
  startingBalance: Money;
  endingBalance: Money;
  netDeposits?: Money;
  totalEarned?: Money;
  adjustments?: Money;
}> = ({ id, startDate, endDate, startingBalance, endingBalance, netDeposits, totalEarned, adjustments }) => {
  return (
    <HiddenChartDescription id={id}>
      Starting balance {formatDate(startDate)}: {formatMoney(startingBalance)}.
      {netDeposits && ` Net deposits: ${formatMoney(netDeposits)}.`}
      {totalEarned && ` Total earned: ${formatMoney(totalEarned)}.`}
      {adjustments && adjustments.cents !== 0 && ` Adjustments: ${formatMoney(adjustments)}. `}
      Ending balance {formatDate(endDate)}: {formatMoney(endingBalance)}.
    </HiddenChartDescription>
  );
};

export const BalanceChart: React.FC<BalanceChartProps> = ({
  history,
  loading = true,
  startingBalance,
  endingBalance,
  netDeposits,
  totalEarned,
  adjustments,
}) => {
  const balanceDetailsId = useId();
  const hasTrackedHover = useRef(false);

  const handleChartHover = () => {
    if (!hasTrackedHover.current) {
      trackHover({
        name: 'balanceGraphScrub',
        module: 'Performance',
        location: 'Balance',
      });
      hasTrackedHover.current = true;
    }
  };

  return (
    <Box onMouseEnter={handleChartHover}>
      <ParentSize debounceTime={10}>
        {({ width }) =>
          !loading && history && history.timeSeries.length > 0 ? (
            <>
              {startingBalance && endingBalance && (
                <BalanceChartDescription
                  id={balanceDetailsId}
                  startDate={new Date(history.timeSeries[0].date)}
                  endDate={new Date(history.timeSeries[history.timeSeries.length - 1].date)}
                  startingBalance={startingBalance}
                  endingBalance={endingBalance}
                  netDeposits={netDeposits}
                  totalEarned={totalEarned}
                  adjustments={adjustments}
                />
              )}
              <SharedBalanceChart
                data={history.timeSeries}
                width={width}
                height={350}
                aria-describedby={balanceDetailsId}
                aria-label="Balance Chart"
              />
            </>
          ) : (
            <>
              <Shimmer
                css={{
                  width: '270px',
                  height: 'size.250',
                  mb: 'space.200',
                  mt: 'space.100',
                }}
              />
              <PlaceholderAreaChart
                width={width}
                height={350}
                variant={loading || !history ? 'loading' : 'empty'}
                aria-describedby={balanceDetailsId}
                aria-label={!history ? 'Loading chart' : 'No data available'}
              />
            </>
          )
        }
      </ParentSize>
    </Box>
  );
};
